import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { DestinationDetail, SurfBreak, SpotScore } from '@/data/destination-details/types';

interface DbBreak {
  id: string;
  break_name: string;
  description: string | null;
  image_url: string | null;
  wave_quality_score: number | null;
  consistency_score: number | null;
  crowd_score: number | null;
  accessibility_score: number | null;
  display_order: number | null;
  skill_level: string[] | string | null;
  ideal_tide: string[] | string | null;
  swell_range: string[] | string | null;
  bottom: string[] | string | null;
  direction: string[] | string | null;
  wave_type: string[] | string | null;
}

interface DbActivity {
  id: string;
  activity_name: string | null;
  description: string | null;
  image_url: string | null;
  display_order: number | null;
}

interface DbFaq {
  id: string;
  question: string | null;
  answer: string | null;
  display_order: number | null;
}

interface DbDestinationFull {
  id: string;
  slug: string;
  name: string;
  lat: number | null;
  lng: number | null;
  summary: string | null;
  summary_long: string | null;
  hero_image_url: string | null;
  surf_culture: string | null;
  getting_there: string | null;
  accommodation_note: string | null;
  access_note: string | null;
  swell_consistency: number | null;
  crowd_score: number | null;
  budget_score: number | null;
  water_temp_score: number | null;
  safety_score: number | null;
  has_lessons: boolean | null;
  has_board_rental: boolean | null;
  has_guiding: boolean | null;
  flight_cost_europe: string | null;
  flight_cost_na: string | null;
  flight_cost_aus: string | null;
  travel_time_europe: string | null;
  travel_time_na: string | null;
  travel_time_aus: string | null;
  countries: { name: string } | null;
  destination_breaks: DbBreak[];
  destination_activities: DbActivity[];
  destination_faqs: DbFaq[];
}

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1502680390469-be75c86b636f?w=1920&q=80';

// Coerce any value (array, string, null) into a lower-case string for matching.
const asText = (val: string[] | string | null | undefined): string => {
  if (!val) return '';
  if (Array.isArray(val)) return val.join(' ').toLowerCase();
  return String(val).toLowerCase();
};

// Coerce to a display string (preserving casing) joining arrays with a separator.
const asDisplay = (
  val: string[] | string | null | undefined,
  separator = ' / ',
  fallback = '—'
): string => {
  if (val == null) return fallback;
  if (Array.isArray(val)) return val.length ? val.join(separator) : fallback;
  const s = String(val).trim();
  return s || fallback;
};

const normaliseWaveType = (val: string[] | string | null): SurfBreak['waveType'] => {
  const v = asText(val);
  if (v.includes('point') && v.includes('reef')) return 'Point/Reef';
  if (v.includes('point')) return 'Point';
  if (v.includes('reef')) return 'Reef';
  return 'Beach break';
};

const normaliseDirection = (val: string[] | string | null): SurfBreak['direction'] => {
  const v = asText(val);
  if (v.includes('left') && v.includes('right')) return 'Left and Right';
  if (v.includes('left')) return 'Left';
  return 'Right';
};

const skillToString = (skill: string[] | string | null): string => {
  if (!skill) return 'Intermediate';
  if (Array.isArray(skill)) return skill.length ? skill.join(' / ') : 'Intermediate';
  return skill;
};

const skillToDifficulty = (skill: string[] | string | null): number => {
  const s = skillToString(skill).toLowerCase();
  if (s.includes('expert')) return 10;
  if (s.includes('advanced') && s.includes('intermediate')) return 7;
  if (s.includes('advanced')) return 9;
  if (s.includes('intermediate')) return 5;
  if (s.includes('beginner')) return 2;
  return 5;
};

// Format swell range — arrays in the DB look like ["1","5"] meaning 1–5 ft.
const formatSwellRange = (val: string[] | string | null): string => {
  if (!val) return '—';
  if (Array.isArray(val)) {
    if (val.length === 0) return '—';
    if (val.length === 2) return `${val[0]}–${val[1]} ft`;
    return val.join(', ');
  }
  return String(val);
};

const adaptBreak = (b: DbBreak): SurfBreak => ({
  name: b.break_name,
  image: b.image_url ?? undefined,
  waveType: normaliseWaveType(b.wave_type),
  direction: normaliseDirection(b.direction),
  swellRange: formatSwellRange(b.swell_range),
  idealTide: asDisplay(b.ideal_tide),
  skillLevel: skillToString(b.skill_level),
  crowdFactor: b.crowd_score ?? 5,
  consistencyRating: b.consistency_score ?? b.wave_quality_score ?? 5,
  accessScore: b.accessibility_score ?? undefined,
  difficultyScore: skillToDifficulty(b.skill_level),
  bottomType: Array.isArray(b.bottom) ? (b.bottom.length ? b.bottom.join(' / ') : undefined) : b.bottom ?? undefined,
  description: b.description ?? '',
});

const buildSpotScores = (d: DbDestinationFull): SpotScore[] => {
  const scores: SpotScore[] = [];
  if (d.swell_consistency != null)
    scores.push({ category: 'Swell Consistency', score: d.swell_consistency, notes: '' });
  if (d.water_temp_score != null)
    scores.push({ category: 'Water Temp', score: d.water_temp_score, notes: '' });
  if (d.crowd_score != null)
    scores.push({ category: 'Crowd Level', score: d.crowd_score, notes: '' });
  if (d.safety_score != null)
    scores.push({ category: 'Safety Score', score: d.safety_score, notes: '' });
  if (d.budget_score != null)
    scores.push({ category: 'Budget Level', score: d.budget_score, notes: '' });
  return scores;
};

const buildSurfInfoDescription = (d: DbDestinationFull): string => {
  const parts: string[] = [];
  if (d.surf_culture) parts.push(d.surf_culture);
  if (d.has_lessons) parts.push('Surf lessons widely available from local schools and camps.');
  if (d.has_board_rental) parts.push('Board rentals are easy to find in town.');
  if (d.has_guiding) parts.push('Surf guides can be arranged for harder-to-reach breaks.');
  return parts.join(' ') || 'Local surf schools, rentals and guides operate throughout the season.';
};

const sortByDisplayOrder = <T extends { display_order: number | null; id: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999) || a.id.localeCompare(b.id));

const buildActivitiesBlog = (d: DbDestinationFull): DestinationDetail['activitiesBlog'] | undefined => {
  const activities = sortByDisplayOrder(d.destination_activities ?? [])
    .filter((activity) => activity.activity_name || activity.description)
    .map((activity) => ({
      title: activity.activity_name || 'Local activity',
      content: activity.description || 'A worthwhile option between surf sessions.',
      image: activity.image_url || undefined,
    }));

  if (!activities.length) return undefined;

  return {
    intro: `When the surf window closes, ${d.name} still has plenty to explore between sessions.`,
    sections: activities,
  };
};

const buildFaqs = (d: DbDestinationFull): DestinationDetail['faqs'] =>
  sortByDisplayOrder(d.destination_faqs ?? [])
    .filter((faq) => faq.question && faq.answer)
    .map((faq) => ({ question: faq.question!, answer: faq.answer! }));

const DETAIL_SELECT = `id,slug,name,lat,lng,summary,summary_long,hero_image_url,surf_culture,getting_there,
  accommodation_note,access_note,swell_consistency,crowd_score,budget_score,water_temp_score,
  safety_score,has_lessons,has_board_rental,has_guiding,
  flight_cost_europe,flight_cost_na,flight_cost_aus,travel_time_europe,travel_time_na,travel_time_aus,
  countries(name),
  destination_breaks(id,break_name,description,image_url,wave_quality_score,consistency_score,
    crowd_score,accessibility_score,display_order,skill_level,ideal_tide,swell_range,bottom,
    direction,wave_type),
  destination_activities(id,activity_name,description,image_url,display_order),
  destination_faqs(id,question,answer,display_order)`;

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const resolveDestination = async (param: string): Promise<DbDestinationFull | null> => {
  // 1) Exact slug match (covers cards that already link with the DB slug).
  const exact = await supabase
    .from('destinations')
    .select(DETAIL_SELECT)
    .eq('slug', param)
    .eq('is_active', true)
    .maybeSingle();
  if (exact.error) throw exact.error;
  if (exact.data) return exact.data as unknown as DbDestinationFull;

  // 2) Fallback: scan active destinations and match by slugified slug/name (+country).
  // Handles links like `cape-town-south-africa` or `rincon-puerto-rico` that don't
  // line up with the DB's free-form slugs.
  interface SlugMatchRow {
    id: string;
    slug: string | null;
    name: string | null;
    countries: { name: string }[] | null;
  }

  const list = await supabase
    .from('destinations')
    .select('id,slug,name,countries(name)')
    .eq('is_active', true);
  if (list.error) throw list.error;

  const target = slugify(param);
  const match = (list.data ?? []).find((row: SlugMatchRow) => {
    const dbSlug = (row.slug ?? '').toString().trim();
    if (dbSlug && slugify(dbSlug) === target) return true;
    const name = (row.name ?? '').toString().trim();
    if (name && slugify(name) === target) return true;
    const country = row.countries?.[0]?.name ?? '';
    if (name && country && slugify(`${name} ${country}`) === target) return true;
    return false;
  });
  if (!match) return null;

  const full = await supabase
    .from('destinations')
    .select(DETAIL_SELECT)
    .eq('id', match.id)
    .maybeSingle();
  if (full.error) throw full.error;
  return (full.data as unknown as DbDestinationFull) ?? null;
};

export const useDestinationDetail = (slug: string | undefined) => {
  return useQuery({
    queryKey: ['destination-detail', slug],
    enabled: !!slug,
    queryFn: async (): Promise<(DestinationDetail & { dbId: string }) | null> => {
      let data: DbDestinationFull | null;
      try {
        data = await resolveDestination(slug!);
      } catch (err) {
        // Real error goes to console for debugging; callers must not render
        // this directly — see getFriendlyErrorMessage in src/lib/errors.ts
        // (same raw-PostgREST-message defect discovered here too).
        console.error('Failed to load destination detail:', err);
        throw err;
      }
      if (!data) return null;

      const d = data as unknown as DbDestinationFull;

      const breaks = sortByDisplayOrder(d.destination_breaks ?? []).map(adaptBreak);

      const detail: DestinationDetail & { dbId: string } = {
        id: d.slug,
        dbId: d.id,
        name: d.name,
        country: d.countries?.name ?? '',
        heroImage: d.hero_image_url || FALLBACK_IMAGE,
        coordinates: { lat: d.lat ?? 0, lng: d.lng ?? 0 },
        description: d.summary_long || d.summary || '',
        spotScores: buildSpotScores(d),
        gettingThere: {
          airport: d.getting_there || 'Nearest international airport',
          airportCode: '',
          distanceFromAirport: '—',
          flightCosts: {
            europe: d.flight_cost_europe || '—',
            usa: d.flight_cost_na || '—',
            australia: d.flight_cost_aus || '—',
          },
          transportOptions: [],
        },
        breaks,
        surfInfo: {
          description: buildSurfInfoDescription(d),
          lessonPrice: d.has_lessons ? 'Lessons available locally' : 'Limited',
          rentalInfo: d.has_board_rental ? 'Board rentals in town' : 'Bring your own quiver',
          repairServices: 'Ask at the local surf shops',
        },
        otherActivities: sortByDisplayOrder(d.destination_activities ?? [])
          .map((activity) => activity.activity_name)
          .filter((activity): activity is string => !!activity),
        activitiesBlog: buildActivitiesBlog(d),
        faqs: buildFaqs(d),
      };

      return detail;
    },
    // Scoped override for this call site only — see useDestinations.ts for
    // the same pattern and rationale.
    retry: 1,
    retryDelay: 1000,
  });
};
